import { useCallback, useEffect, useMemo, useState, Fragment } from "react";
import { useTranslation } from "react-i18next";

// Redux
import { useDispatch } from "react-redux";
import { openSnackbar } from "Store/SnackbarSlice";

// MUI
import {
  Typography,
  CircularProgress,
  Backdrop,
  Stack,
  Divider,
} from "@mui/material";

// Components
import ViewMoreAppointmentsModal from "../ViewMoreAppointmentsModal";
import BookAppointmentModal from "../BookAppointmentModal/BookAppointmentModal";
import { fetchAppointments } from "../Common/fetchAppointments";
import ClinicCard from "./ClinicCard";
import AppointmentFilters from "../AppointmentFilters";
import { getLocalizedDate } from "Components/Language/Common";
import NoAppointmentsText from "./NoAppointmentsText";

import { DateTime } from "luxon";

// Styles
import { styles } from "../styles";

// Utils
import { getPatientType } from "Utils/PatientManagementUtils";

// Constants
import {
  HALF_SECOND,
  FIVE_MINUTES,
  MANITOBA_TIMEZONE,
  TWO_SECONDS,
} from "Constants/TimeConstants";
import {
  AVAILABILITY_STATUS_PRIORITY,
  APPOINTMENT_AVAILABILITY_STATUS,
} from "Constants/AppointmentConstants";
const { ONLINE_AVAILABLE, WALK_IN_AVAILABLE, FULLY_BOOKED } =
  APPOINTMENT_AVAILABILITY_STATUS;

const AvailableAppointments = ({
  patient,
  client,
  reasonForVisit = "",
  additionalInfo = "",
  selectedLocation = "",
  initialSlots = [],
  isMBS = false,
}) => {
  const TODAY = DateTime.now().setZone(MANITOBA_TIMEZONE).toISODate();
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const [availableSlots, setAvailableSlots] = useState(initialSlots);
  const [loading, setLoading] = useState(true);
  const [loadingBackdrop, setLoadingBackdrop] = useState(false);
  const [groupedSlots, setGroupedSlots] = useState([]);
  const [openBookAppointmentModal, setOpenBookAppointmentModal] =
    useState(false);
  const [openViewMoreModal, setOpenViewMoreModal] = useState(false);
  const [selectedClinic, setSelectedClinic] = useState(null);
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [isTimeSelected, setIsTimeSelected] = useState(false);

  useEffect(() => {
    setAvailableSlots(initialSlots);
  }, [initialSlots]);

  const successfulClinics = useMemo(
    () => (availableSlots || []).filter((c) => !c.apiError),
    [availableSlots]
  );

  const failedClinics = useMemo(
    () => (availableSlots || []).filter((c) => c.apiError && c.Clinic),
    [availableSlots]
  );

  const totalClinics = useMemo(
    () => (availableSlots || []).length,
    [availableSlots]
  );

  const hasAnyAppointments = useMemo(
    () =>
      successfulClinics.some(
        (c) => c.availableSlots?.length > 0 || c.availabilityStatus
      ),
    [successfulClinics]
  );

  const [cardDateSelected, setCardDateSelected] = useState(TODAY);
  const walkIn = t("availableAppointments.walkIn");
  const fullyBooked = t("availableAppointments.fullyBooked");

  const handleViewMoreModal = (currentClinic, currentDate) => {
    setCardDateSelected(currentDate);
    setSelectedClinic(currentClinic);
    setOpenViewMoreModal(true);
  };

  // Refresh available slots every 5 minutes
  useEffect(() => {
    const refreshInterval = setInterval(() => {
      refreshBackground();
    }, FIVE_MINUTES);
    return () => {
      clearInterval(refreshInterval);
    };
  });

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setLoading(false);
    }, HALF_SECOND);

    return () => clearTimeout(timeoutId);
  }, []);

  const createAppointment = (clinic, date, time) => {
    const appointment = {
      patientId: patient.id,
      inSystem: patient.inSystem ?? true,
      patientType: getPatientType(patient.inSystem),
      clinicId: clinic.Clinic.id,
      clinicName: t("clinic.name", { clinic: clinic.Clinic }),
      clinicAddress: t("clinic.address", { clinic: clinic.Clinic }),
      date: date,
      startTime: time.startTime,
      endTime: time.endTime,
      type: reasonForVisit,
      notes: additionalInfo,
      accuroSlots: time.uniqueSlots,
    };
    setSelectedAppointment(appointment);
    setOpenBookAppointmentModal(true);
  };

  const refreshAppointments = async () => {
    try {
      const availableSlots = await fetchAppointments(
        patient,
        reasonForVisit,
        selectedLocation,
        null,
        client?.id
      );
      setAvailableSlots(availableSlots);

      // refresh view more modal if it is open
      setSelectedClinic((prev) => {
        const updatedRow = availableSlots.find((slot) => slot.id === prev?.id);

        // Close modal if selected row has no available slots
        if (!updatedRow) {
          setOpenViewMoreModal(false);
        }

        return updatedRow || null;
      });
    } catch (error) {
      const defaultErrorMessage = t("errors.errorRefreshingAppointments");
      dispatch(
        openSnackbar({ message: defaultErrorMessage, severity: "error" })
      );
    }
  };

  const refreshBackground = async () => {
    // refresh appointments in the background
    await refreshAppointments();
    setLoadingBackdrop(true);
    // show loading spinner for 2 seconds
    setTimeout(() => {
      setLoadingBackdrop(false);
    }, TWO_SECONDS);
  };

  const setFilteredAppointments = useCallback(
    (filtered) => {
      const groupSlots = filtered.reduce((acc, slot) => {
        const addToGroup = (groupingKey) => {
          if (!acc[groupingKey]) {
            acc[groupingKey] = [];
          }

          // Use a Set to track unique clinic names for this grouping
          const clinicNamesSet = new Set(
            acc[groupingKey].map((s) => s.Clinic.name)
          );

          if (!clinicNamesSet.has(slot.Clinic.name)) {
            acc[groupingKey].push(slot);
          }
        };
        // Iterate through each dateKey in availabilityStatus
        Object.keys(slot.availabilityStatus).forEach((dateKey) => {
          const status = slot.availabilityStatus[dateKey];

          if (status === ONLINE_AVAILABLE) {
            slot.availableSlots.forEach((availableSlot) => {
              if (availableSlot.date === dateKey) {
                addToGroup(availableSlot.date);
              }
            });
          } else if (status === WALK_IN_AVAILABLE) {
            addToGroup(walkIn);
          } else if (status === FULLY_BOOKED) {
            addToGroup(fullyBooked);
          }
        });

        return acc;
      }, {});

      setGroupedSlots(groupSlots);
    },
    [fullyBooked, walkIn]
  );

  const getGroupPriority = (key) => {
    if (DateTime.fromISO(key).isValid)
      return AVAILABILITY_STATUS_PRIORITY[ONLINE_AVAILABLE];
    if (key === walkIn) return AVAILABILITY_STATUS_PRIORITY[WALK_IN_AVAILABLE];
    if (key === fullyBooked) return AVAILABILITY_STATUS_PRIORITY[FULLY_BOOKED];
    return 99; // fallback for unknown keys
  };

  const getPriorityGroups = (a, b) => {
    const aPriority = getGroupPriority(a);
    const bPriority = getGroupPriority(b);

    if (aPriority !== bPriority) {
      return aPriority - bPriority;
    }
    // If both are dates, sort from old to new
    if (aPriority === AVAILABILITY_STATUS_PRIORITY[ONLINE_AVAILABLE]) {
      return DateTime.fromISO(a) - DateTime.fromISO(b);
    }
    // Otherwise, keep original order
    return 0;
  };

  return (
    <Stack spacing={2} sx={{ boxSizing: "border-box", alignItems: "center" }}>
      <Backdrop sx={styles.loadingBackdrop} open={loadingBackdrop}>
        <CircularProgress color="inherit" />
      </Backdrop>
      {loading ? (
        <Stack
          sx={{
            minHeight: "50vh",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <CircularProgress color="primary" />
        </Stack>
      ) : (
        <>
          {failedClinics.length > 0 && (
            <NoAppointmentsText
              failedClinics={failedClinics}
              totalClinics={totalClinics}
              locationName={selectedLocation}
            />
          )}
          {hasAnyAppointments && (
            <AppointmentFilters
              successfulClinicAppointments={successfulClinics}
              failedClinicAppointments={failedClinics}
              onChange={setFilteredAppointments}
              setIsTimeSelected={setIsTimeSelected}
              isMBS={isMBS}
            />
          )}
          {groupedSlots && Object.keys(groupedSlots).length > 0 ? (
            Object.keys(groupedSlots)
              .sort((a, b) => getPriorityGroups(a, b))
              .map((groupingTitle) => (
                <Fragment key={groupingTitle}>
                  <Divider sx={{ width: "100%" }}>
                    <Typography variant="h6" color="primary.main">
                      {DateTime.fromISO(groupingTitle).isValid
                        ? getLocalizedDate(groupingTitle, "cccc, MMM d", t)
                        : groupingTitle}
                    </Typography>
                  </Divider>

                  {groupedSlots[groupingTitle]
                    .slice()
                    .sort((a, b) => {
                      const nameA = t("clinic.name", {
                        clinic: a.Clinic,
                      }).toLowerCase();
                      const nameB = t("clinic.name", {
                        clinic: b.Clinic,
                      }).toLowerCase();
                      return nameA.localeCompare(nameB, "fr", {
                        sensitivity: "base",
                      });
                    })
                    .map((clinicSlot, index) => (
                      <ClinicCard
                        key={clinicSlot.id || index}
                        clinic={clinicSlot}
                        groupingTitle={groupingTitle}
                        createAppointment={createAppointment}
                        handleViewMoreModal={handleViewMoreModal}
                        isTimeSelected={isTimeSelected}
                        isMBS={isMBS}
                        today={TODAY}
                      />
                    ))}
                </Fragment>
              ))
          ) : (
            <Typography sx={{ p: 2, textAlign: "center" }}>
              {t("viewMoreAppointmentsModal.noAppointmentsAvailable")}
            </Typography>
          )}

          {openViewMoreModal && (
            <ViewMoreAppointmentsModal
              clinic={selectedClinic}
              currentDate={cardDateSelected}
              createAppointment={createAppointment}
              openModal={openViewMoreModal}
              closeModal={() => setOpenViewMoreModal(false)}
            />
          )}
          {openBookAppointmentModal && (
            <BookAppointmentModal
              patient={patient}
              client={client}
              closeModal={() => setOpenBookAppointmentModal(false)}
              appointment={selectedAppointment}
              refreshAppointments={refreshAppointments}
              isMBS={isMBS}
            />
          )}
        </>
      )}
    </Stack>
  );
};

export default AvailableAppointments;
